import React, { ReactNode, useEffect, useState } from 'react'
import { CampaignStatus } from 'types/campaign'
import Preview from 'components/Preview'
import FlexCol from 'components/Layout/FlexCol'
import { useTranslation } from 'react-i18next'
import Button, { ButtonSize, ButtonTheme } from 'components/Form/Button'
import { Routes } from 'routes'
import Link from 'components/Link'
import TsxUtils from 'utils/tsx'
import TooltipHelp from 'components/TooltipHelp'
import './VoohCampaignDetailsCreations.scss'
import CreationHelper from 'utils/creation-helper'
import DateUtils from '../../../../utils/date'
import { DATE_FORMAT } from '../../../../constant'
import { VoohCampaignForm, VoohCreation } from '../../VoohCampaignForm/types'
import { groupedVoohCreationsDefault, VoohGroupedCreations } from './types'

const VoohCampaignDetailsCreations: React.FC<{
  voohCampaignForm: VoohCampaignForm
  campaignStatus: CampaignStatus
}> = ({ voohCampaignForm, campaignStatus }) => {
  const { t } = useTranslation()

  const [icon, setIcon] = useState<string>()
  const [groupedCreations, setGroupedCreations] = useState<VoohGroupedCreations>(
    groupedVoohCreationsDefault()
  )

  const loadVoohUploudIcon = (): void => {
    import(`images/dg-cs.svg`).then(image => void setIcon(image.default))
  }

  useEffect(() => {
    loadVoohUploudIcon()
  }, [])

  useEffect(() => {
    const tempGC: VoohGroupedCreations = groupedVoohCreationsDefault()

    const sortedCreations = [...voohCampaignForm.creations].sort(
      (a: VoohCreation, b: VoohCreation) => a.file!.filename.localeCompare(b.file!.filename)
    )

    sortedCreations.forEach((c: VoohCreation) => {
      tempGC[c.mediaFormat].push(c)
    })

    setGroupedCreations(tempGC)
  }, [voohCampaignForm.creations])

  const Container: React.FC<{ children: ReactNode }> = ({ children }) => (
    <div className='CampaignDetailsCreations__container'>{children}</div>
  )

  const Item = (creation: VoohCreation): ReactNode => (
    <>
      <div className='CampaignDetailsCreations__item--preview-container'>
        {creation.archivedAt && (
          <TooltipHelp containerClassName='CampaignDetailsCreations__item--preview-tooltip'>
            {t('common.archivedInfo')}
          </TooltipHelp>
        )}
        <Preview
          className={
            'CampaignDetailsCreations__item--preview' +
            TsxUtils.extraStyle(creation.archivedAt, 'CampaignDetailsCreations--grayed-out')
          }
          src={creation.file?.url || ''}
          thumbnailSrc={creation.thumbnailUrl}
          isVideo={creation.file?.isVideo}
          alt={creation.file?.filename || ''}
        />
      </div>

      <div className={`CampaignDetailsCreations__item--info`}>
        <div
          className={
            'CampaignDetailsCreations__item--name' +
            TsxUtils.extraStyle(creation.archivedAt, 'CampaignDetailsCreations--grayed-out')
          }
        >
          {creation.file?.filename}
        </div>

        <div className='CampaignDetailsCreations__item-details--container'>
          <p className='CampaignDetailsCreations__item-details--date'>
            {CreationHelper.getCreationDateText(creation)}
          </p>

          {creation.schedules && creation.schedules.length > 1 && (
            <div>
              <TooltipHelp containerClassName='CreationSchedules'>
                <div className='CreationSchedules__details'>
                  <div className='CreationSchedules__header'>
                    <p>{`${t('common.scheduleDates')}`}:</p>
                  </div>
                  <div>
                    {creation.schedules.map((schedule, index) => (
                      <p key={index}>
                        {`${DateUtils.parseAndFormat(
                          schedule.startDate,
                          DATE_FORMAT
                        )} - ${DateUtils.parseAndFormat(schedule.endDate, DATE_FORMAT)}`}
                      </p>
                    ))}
                  </div>
                </div>
              </TooltipHelp>
            </div>
          )}
        </div>

        <div className='CampaignDetailsCreations__creations-details'>
          <p>
            {t('common.creationDetails')}: {creation.details}
          </p>
        </div>

        <div className='CampaignDetailsCreations__creations-details'>
          <p>
            {t('campaignDetails.emissionTime')}: {creation.duration}s
          </p>
        </div>
        <div className='CampaignDetailsCreations__creations-details'>
          {creation.hourFrequency && (
            <p>
              {t('form.campaign.details.hourFrequency')}: {creation.hourFrequency}
            </p>
          )}
        </div>

        <div className='CampaignDetailsCreations__item-details--container'>
          <Link
            className='CampaignDetailsCreations__item-details'
            toUrl={Routes.CAMPAIGNS.EMISSION_SCHEDULE({
              campaignId: voohCampaignForm.id,
              creationId: creation.id,
              readOnly: 'ro',
              vooh: true,
            })}
          >
            {t('common.emissionSchedule')}
          </Link>
        </div>
      </div>
    </>
  )

  return (
    <>
      <div className='CampaignDetailsCreations'>
        {Object.entries(groupedCreations).map(
          ([mediaFormatValue, creations]: [string, VoohCreation[]]) => (
            <FlexCol
              key={mediaFormatValue}
              className='CampaignDetailsCreations__col'
            >
              <div className='CampaignDetailsCreations__header'>
                <img
                  className='CampaignDetailsCreations__header--icon'
                  src={icon}
                  alt={t(`mediaType.vooh`)}
                />
                <div className='CampaignDetailsCreations__header--format'>
                  {t(`mediaType.vooh`)}
                </div>
              </div>

              {creations.length > 0 ? (
                creations
                  .sort(
                    (a: VoohCreation, b: VoohCreation) =>
                      new Date(a.createdAt!).getTime() - new Date(b.createdAt!).getTime()
                  )
                  .map((creation: VoohCreation) => {
                    return <Container key={creation.id}>{Item(creation)}</Container>
                  })
              ) : (
                <Container>{t('campaignDetails.info.noCreations')}</Container>
              )}
            </FlexCol>
          )
        )}
      </div>

      {campaignStatus !== CampaignStatus.FINISHED && (
        <Button
          className='CampaignDetailsCreations__button'
          theme={ButtonTheme.NAVY_BLUE_OUTLINE}
          size={ButtonSize.SMALL}
          toUrl={Routes.VOOH_CAMPAIGNS.EDIT(voohCampaignForm.id)}
        >
          {t('common.edit')}
        </Button>
      )}
    </>
  )
}

export default React.memo(VoohCampaignDetailsCreations)
